import type { ApiUnit, ApiFloorPlan, ApiPortfolioPropertyCount, PortfolioProperty, PricingDisplayMode, SpacesConfigLabels } from '@/types/api'
import type { UnitCardProps, UnitCardPricing } from '@/components/UnitCard'
import type { PlanCardProps, PlanCardVariant } from '@/components/PlanCard'
import type { CommunityCardProps } from '@/components/CommunityCard'
import { getOptions, getLabels, isPortfolio, getPortfolioProperty } from '@/data/config'
import { isUnitOnNotice, onNoticeTagLabel } from '@/utils/onNotice'

const FPO_IMAGE = '/wp-content/plugins/ecs-spaces/public/images/spaces-fpo.png'

/**
 * True when a price value represents a missing or zero price.
 * Backend already guards against $0 via set_unit_price_string(),
 * but this adds a front-end safety net for legal compliance.
 */
function isPriceHidden (price: number | null, priceString?: string): boolean {
	if (price === null || price === 0) return true
	if (priceString === 'Call for pricing') return true
	return false
}

/**
 * True when a price has a usable lease term (a positive number). A price must
 * never be shown without its term (legal requirement), so callers hide the
 * price when this is false. Mirrors Utility::resolve_price_lease_term() in PHP.
 */
export function hasLeaseTerm (term: string | number | null | undefined): boolean {
	return term != null && Number(term) > 0
}

/**
 * Strip the trailing " Sq. Ft." suffix that the PHP FloorPlan object
 * bakes into area.display for Spaces 2. Spaces 3 components render
 * their own "ft²" label, so we need the raw numeric range only.
 */
export function formatAreaDisplay (display: string): string {
	return display.replace(/ Sq\. Ft\.$/i, '')
}

/** Shown in place of a withheld available date. */
export const AVAILABILITY_CONTACT_MESSAGE = 'Contact for availability'

/**
 * The unit's available date, or an override message shown without the "Avail." prefix.
 * On-notice units and units whose date the server withheld get the override; a unit-level
 * "hide available date" shows nothing.
 */
export function resolveUnitAvailability (unit: ApiUnit, isOnNotice: boolean): { availableDate?: string, availabilityOverride?: string } {
	if (isOnNotice) return { availabilityOverride: AVAILABILITY_CONTACT_MESSAGE }
	if (unit.hide_available_date) return {}
	if (unit.available_date_hidden) return { availabilityOverride: AVAILABILITY_CONTACT_MESSAGE }
	return unit.available_on_string ? { availableDate: unit.available_on_string } : {}
}

export function toPhoneHref (phone: string | null | undefined): string | undefined {
	return phone ? `tel:${phone.replace(/[^\d+]/g, '')}` : undefined
}

/** The unit's market property (portfolio mode only). */
export function getUnitProperty (unit: ApiUnit): PortfolioProperty | undefined {
	return unit.engrain_asset_id != null ? getPortfolioProperty(String(unit.engrain_asset_id)) : undefined
}

/**
 * The pricing display mode for an object: its own cached mode, else its
 * portfolio property's mode, else the page config (caches built before the field existed).
 */
export function resolvePricingDisplay (mode: PricingDisplayMode | null | undefined, engrainAssetId?: string | number | null): PricingDisplayMode {
	if (mode) return mode
	const propertyMode = engrainAssetId != null ? getPortfolioProperty(String(engrainAssetId))?.pricingDisplayMode : undefined
	return propertyMode ?? (getOptions().pricingDisplayMode as PricingDisplayMode)
}

/** Unit display name, e.g. "Unit 1201-A", honouring the hideUnitLabel option. */
export function formatUnitName (unitNumber: string): string {
	return getOptions().hideUnitLabel ? unitNumber : `${getLabels().unitLabel} ${unitNumber}`
}

type UnitLabels = Pick<SpacesConfigLabels, 'unitLabel' | 'pluralUnitLabel'>

/** The singular unit label for a count of 1, the plural otherwise. */
export function unitLabelForCount (count: number, labels: UnitLabels = getLabels()): string {
	return count === 1 ? labels.unitLabel : labels.pluralUnitLabel
}

/** "Matching Units" heading for a unit count, e.g. "Matching Homes". */
export function formatMatchingUnitsLabel (count: number, labels: UnitLabels = getLabels()): string {
	return `Matching ${unitLabelForCount(count, labels)}`
}

function mapApiTags (tags: Array<{ name: string }>): Array<{ label: string; color: 'brand' }> {
	return tags.map(t => ({ label: t.name, color: 'brand' as const }))
}

export function mapUnitToCardProps (unit: ApiUnit): UnitCardProps {
	const opts = getOptions()
	const labels = getLabels()

	const isOnNotice = isUnitOnNotice(unit)

	// Determine pricing mode
	let pricing: UnitCardPricing = 'flat'
	let priceRange: string | undefined
	let baseRent: string | undefined
	let hasFees = true

	const priceIsHidden = isOnNotice || unit.price_hidden || opts.hidePa || opts.hideStartingAt ||
		isPriceHidden(unit.price) ||
		!hasLeaseTerm(unit.default_lease_term)

	if (priceIsHidden) {
		pricing = 'hidden'
	} else if (opts.useRevManagement && opts.applyProvider !== 'None') {
		pricing = 'revenue-management'
	}

	if (!priceIsHidden) {
		const mode = resolvePricingDisplay(unit.pricing_display, unit.engrain_asset_id)
		if (mode === 'total' || mode === 'both') {
			priceRange = unit.price_with_fees_string
			if (mode === 'both') {
				baseRent = unit.price_string
			}
		} else {
			// mode === 'base' or anything else — price_string is primary, no fees line
			baseRent = unit.price_string
			hasFees = false
		}
	}

	const { availableDate, availabilityOverride } = resolveUnitAvailability(unit, isOnNotice)

	// Tags. On-notice units lead with an "Application Available {date}" brand tag.
	const tags = [
		...(isOnNotice ? [{ label: onNoticeTagLabel(unit), color: 'brand' as const }] : []),
		...mapApiTags(Array.isArray(unit.tags) ? unit.tags : []),
		...(unit.floor_plan ? mapApiTags(Array.isArray(unit.floor_plan.tags) ? unit.floor_plan.tags : []) : [])
	]

	// Special
	const specialMessage = unit.specials ?? undefined

	// Virtual tour
	const hasVirtualTour = !!(unit.unit_tour_url || unit.floor_plan?.tour_url)
	const tourUrl = unit.unit_tour_url || unit.floor_plan?.tour_url || undefined

	// Disclaimer symbol: present if the post-text label contains *
	const hasDisclaimerSymbol = !priceIsHidden && labels.unitPricingPostText.includes('*')

	// Portfolio cards identify the community; plan detail isn't available across properties.
	const portfolio = isPortfolio()
	const communityLink = portfolio ? getUnitProperty(unit)?.spacesPageLink : undefined

	return {
		unitNumber: unit.unit_number,
		unitLabel: opts.hideUnitLabel ? '' : labels.unitLabel,
		floorPlanName: portfolio ? undefined : (unit.plan_name ?? ''),
		floorPlanHref: unit.floor_plan ? `#plan/${unit.floor_plan.id}` : '#',
		communityName: portfolio ? unit.spaces_asset_name : undefined,
		communityHref: communityLink?.url,
		communityTarget: communityLink?.target,
		imageUrl: unit.floor_plan?.primary_image_cdn ?? FPO_IMAGE,
		beds: unit.floor_plan?.bedroom_count_string_short ?? String(unit.bedroom_count),
		baths: unit.floor_plan?.bathroom_count_string_short ?? String(unit.bathroom_count),
		sqft: Number(unit.area),
		availableDate,
		availabilityOverride,
		priceRange,
		baseRent,
		leaseTerm: priceIsHidden ? undefined : (unit.default_lease_term ? `${unit.default_lease_term} Mo.` : undefined),
		tags,
		specialMessage,
		hasVirtualTour,
		tourUrl,
		pricing,
		hasFees,
		hasDisclaimerSymbol
	}
}

export function mapPlanToCardProps (plan: ApiFloorPlan): PlanCardProps {
	const opts = getOptions()
	const labels = getLabels()

	// Determine variant
	let variant: PlanCardVariant = 'default'

	if (!plan.is_available) {
		variant = 'unavailable'
	} else if (plan.price_hidden || opts.hidePa || opts.hideStartingAt || isPriceHidden(plan.min_price) || !hasLeaseTerm(plan.min_price_lease_term)) {
		variant = 'no-pricing'
	}

	// Pricing — only populated for 'default' variant
	let totalPrice: string | undefined
	let baseRent: string | undefined
	let leaseTerm: string | undefined
	let hasFees = true

	if (variant === 'default') {
		const mode = resolvePricingDisplay(plan.pricing_display)
		if (mode === 'total' || mode === 'both') {
			totalPrice = plan.starting_at_price_with_required_asset_fees
		} else {
			hasFees = false
		}

		if (mode === 'both' || mode === 'base') {
			baseRent = plan.starting_at_price
		}
		// hasLeaseTerm() already passed: a missing term forces the 'no-pricing' variant above.
		leaseTerm = `${plan.min_price_lease_term} Mo.`
	}

	// Area — unavailable plans don't display area
	const sqftRange = plan.is_available ? formatAreaDisplay(plan.area.display) : ''

	// Tags — "Unavailable" is rendered by the PlanCard variant, not as a tag overlay
	const tags: PlanCardProps['tags'] = []

	if (plan.featured) {
		tags.push({ label: 'Featured', color: 'brand' })
	}
	tags.push(...mapApiTags(plan.tags))

	// Disclaimer symbol
	const hasDisclaimerSymbol = variant === 'default' && labels.planPricingPostText.includes('*')

	return {
		planName: plan.name,
		imageUrl: plan.primary_image_cdn ?? FPO_IMAGE,
		beds: plan.bedroom_count_string_short,
		baths: plan.bathroom_count_string_short,
		sqftRange,
		matchingUnits: plan.available_unit_count,
		matchingUnitsLabel: formatMatchingUnitsLabel(plan.available_unit_count, labels),
		totalPrice,
		baseRent,
		leaseTerm,
		tags,
		hasVirtualTour: !!plan.tour_url,
		tourUrl: plan.tour_url || undefined,
		hasFees,
		hasDisclaimerSymbol,
		variant,
		hideUnavailableTag: opts.hideUnavailableTag
	}
}

export function mapPropertyToCommunityCardProps (property: PortfolioProperty, counts: ApiPortfolioPropertyCount): CommunityCardProps {
	const opts = getOptions()
	const labels = getLabels()

	const priceIsHidden = counts.starting_price_hidden || opts.hidePa || opts.hideStartingAt ||
		isPriceHidden(counts.starting_price, counts.starting_price_string ?? undefined) ||
		!hasLeaseTerm(counts.starting_price_lease_term)

	let totalPrice: string | undefined
	let baseRent: string | undefined
	let leaseTerm: string | undefined
	let hasFees = true
	const mode = resolvePricingDisplay(counts.starting_price_pricing_display, property.engrainAssetId)

	if (!priceIsHidden) {
		if (mode === 'total' || mode === 'both') {
			totalPrice = counts.starting_price_with_fees_string ?? undefined
			if (mode === 'both') baseRent = counts.starting_price_string ?? undefined
		} else {
			baseRent = counts.starting_price_string ?? undefined
			hasFees = false
		}
		leaseTerm = counts.starting_price_lease_term ? `${counts.starting_price_lease_term} Mo.` : undefined
	} else if (mode === 'base') {
		hasFees = false
	}

	const phoneHref = toPhoneHref(property.phone)
	const contactLink = property.contactLink ??
		(phoneHref ? { url: phoneHref, title: 'Contact', target: '' } : null)

	return {
		name: property.name,
		imageUrl: property.imageUrl || FPO_IMAGE,
		phone: property.phone ?? undefined,
		phoneHref,
		matchingUnits: counts.available_unit_count,
		matchingUnitsLabel: formatMatchingUnitsLabel(counts.available_unit_count, {
			unitLabel: property.unitLabel || labels.unitLabel,
			pluralUnitLabel: property.pluralUnitLabel || labels.pluralUnitLabel
		}),
		totalPrice,
		baseRent,
		leaseTerm,
		hasFees,
		hasDisclaimerSymbol: !priceIsHidden && labels.planPricingPostText.includes('*'),
		// Base rent isn't a total monthly price, so base-only cards keep CardPricing's default pre-text.
		pricingPreText: mode === 'base' ? undefined : (labels.communityPricingPreText || undefined),
		pricing: priceIsHidden ? 'hidden' : 'default',
		contactLink
	}
}
